// src/components/module/ventas/facturas/TicketPOS.tsx
import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font
} from '@react-pdf/renderer';
import { DetalleVenta } from '../../../../components/types/ventas.types';
import { formatMoney, formatDate } from '../../../../utils/format';
import { PDFQRCode } from './PDFQRCode';
import { DocumentoElectronico } from '../../../../services/api/facturacionElectronicaApi';
import { contenidoQrDian } from '../../../../utils/dian';

const styles = StyleSheet.create({
    dianSection: {
        alignItems: 'center',
        marginTop: 4,
        marginBottom: 2,
    },
    cufeTitulo: {
        fontSize: 6,
        fontWeight: 'bold',
        marginTop: 2,
    },
    cufeValor: {
        fontSize: 5,
        textAlign: 'center',
    },
    page: {
        padding: '5mm',
        fontFamily: 'Helvetica',
        fontSize: 8,
        width: '80mm',
        alignItems: 'center', // Centrar todo el contenido
    },
    header: {
        width: '100%',
        alignItems: 'center',
        marginBottom: 10,
    },
    divider: {
        width: '100%',
        borderBottomWidth: 1,
        borderBottomStyle: 'dashed',
        borderBottomColor: '#000',
        marginVertical: 5,
    },
    infoRow: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: 1,
    },
    itemRow: {
        width: '100%',
        marginVertical: 2,
    },
    itemDetails: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    totalesSection: {
        width: '100%',
        marginTop: 5,
    },
    impuestosSection: {
        width: '100%',
        marginTop: 5,
    },
    impuestoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: 1,
    },
    footer: {
        marginTop: 10,
        alignItems: 'center',
    },
    paymentSection: {
        width: '100%',
        alignItems: 'center',
        marginVertical: 5,
    }
});

type TicketPOSProps = {
    venta: DetalleVenta;
    /** Documento electronico validado por la DIAN (si la venta se facturo). */
    documentoElectronico?: DocumentoElectronico | null;
    // configuracionEmpresa: ConfiguracionEmpresa;
};

export const TicketPOS: React.FC<TicketPOSProps> = ({ venta, documentoElectronico }) => (
    <Document>
        <Page size={[226.8, 'auto']} style={styles.page}>
            {/* Header */}
            <View style={styles.header}>
                <Text>Nombre Empresa</Text>
                <Text>NIT: 000-000-000</Text>
                <Text>Dirección de la empresa</Text>
                <Text>Tel: 3002428645</Text>
            </View>

            <View style={styles.divider} />

            {/* Información de la factura */}
            <View style={styles.header}>
                <Text>FACTURA DE VENTA N° {venta.numeroFactura}</Text>
                <Text>Fecha: {formatDate(venta.fecha)}</Text>
                <Text>Cliente: {venta.cliente.nombre}</Text>
                {/*  <Text>NIT/CC: {venta.cliente.identificacion}</Text> */}
            </View>

            <View style={styles.divider} />

            {/* Artículos */}
            {venta.articulos.map((articulo, index) => (
                <View key={index} style={styles.itemRow}>
                    <Text>{articulo.Nombre}</Text>
                    <View style={styles.itemDetails}>
                        <Text>{`${articulo.Cantidad} x ${formatMoney(articulo.PrecioVenta)}`}</Text>
                        <Text>{formatMoney(articulo.Valor)}</Text>
                    </View>
                </View>
            ))}

            <View style={styles.divider} />

            {/* Totales y Forma de Pago */}
            <View style={styles.totalesSection}>
                {/* Subtotales */}
                <View style={styles.infoRow}>
                    <Text>Subtotal:</Text>
                    <Text>{formatMoney(venta.subtotal)}</Text>
                </View>

                {/* Descuentos si existen */}
                {venta.descuentoTotal > 0 && (
                    <View style={styles.infoRow}>
                        <Text>Descuento:</Text>
                        <Text>-{formatMoney(venta.descuentoTotal)}</Text>
                    </View>
                )}

                {/* IVA */}
                <View style={styles.infoRow}>
                    <Text>IVA:</Text>
                    <Text>{formatMoney(venta.impuestos[0]?.valor || 0)}</Text>
                </View>

                {/* Total */}
                <View style={[styles.infoRow, { marginTop: 3 }]}>
                    <Text style={{ fontWeight: 'bold' }}>TOTAL:</Text>
                    <Text style={{ fontWeight: 'bold' }}>{formatMoney(venta.totalAPagar)}</Text>
                </View>
            </View>

            <View style={styles.divider} />

            {/* Forma de Pago */}
            <View style={styles.paymentSection}>
                <Text>Forma de Pago: {venta.tipoPago.nombre}</Text>
            </View>

            <View style={styles.divider} />

            {documentoElectronico?.CUFE && (
                <View style={styles.dianSection}>
                    <PDFQRCode value={contenidoQrDian(documentoElectronico) ?? ''} size={70} />
                    <Text style={styles.cufeTitulo}>CUFE</Text>
                    <Text style={styles.cufeValor}>{documentoElectronico.CUFE}</Text>
                    {documentoElectronico.NumeroResolucion && (
                        <Text style={styles.cufeValor}>
                            Resolución DIAN {documentoElectronico.NumeroResolucion}
                        </Text>
                    )}
                </View>
            )}

            <View style={styles.footer}>
                <Text>
                    A esta factura de venta aplican las normas relativas a la letra de cambio (artículo 5 Ley 1231 de 2008).
                    Con esta el Comprador declara haber recibido real y materialmente las mercancías o prestación de servicios descritos en este título.
                </Text>
                <Text>GRACIAS POR SU COMPRA</Text>
            </View>
        </Page>
    </Document>
);